import * as THREE from 'three';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { Section } from './Section.js';
import gsap from 'gsap';

export class World {
    constructor(container) {
        this.container = container;
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this.scene = new THREE.Scene();
        // this.scene.fog = new THREE.FogExp2(0x1a1510, 0.015); // Fog disabled for color testing
        this.camera = new THREE.PerspectiveCamera(75, this.width / this.height, 0.1, 1000);

        // WebGL Renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });

        // CSS3D Renderer (instantiated here, but configured in setupRenderer)
        this.cssRenderer = new CSS3DRenderer();

        this.setupRenderer();
        this.setupCamera();
        this.addObjects();
        this.addLights();

        this.raycaster = new THREE.Raycaster();
        this.pointer = new THREE.Vector2();

        this.setupInteraction();
    }

    setupInteraction() {
        // We need to enable pointer events on WebGL canvas ONLY when hovering interactive objects
        // Otherwise, we let events pass through to the CSS layer (YouTube iframes)

        // Hover Listener
        window.addEventListener('mousemove', (event) => {
            this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
            this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

            this.raycaster.setFromCamera(this.pointer, this.camera);

            // Raycast against interactive objects (Content Hubs)
            // We need to traverse to find them
            const intersects = this.raycaster.intersectObjects(this.scene.children, true);

            let hoveringInteractive = false;

            if (intersects.length > 0) {
                let object = intersects[0].object;
                // Debug Raycast
                if (window.debugRaycast) {
                    console.log('Hit:', object.name || object.type, object.uuid, 'Parent:', object.parent ? (object.parent.name || object.parent.type) : 'null');
                    let tmp = object;
                    while (tmp) {
                        if (tmp.userData && tmp.userData.type) console.log(' - UserData:', tmp.userData);
                        tmp = tmp.parent;
                    }
                }

                // Check if we hit a content hub or something that needs WebGL interaction
                while (object && object !== this.scene) {
                    if (object.userData) {
                        if (object.userData.type === 'content-hub' ||
                            object.userData.type === 'video-button' ||
                            object.userData.type === 'button') {
                            hoveringInteractive = true;
                            break;
                        }
                    }
                    object = object.parent;
                }
            }

            if (hoveringInteractive) {
                this.renderer.domElement.style.pointerEvents = 'auto';
                document.body.style.cursor = 'pointer';
            } else {
                this.renderer.domElement.style.pointerEvents = 'none';
                document.body.style.cursor = 'default';
            }
        });

        // Click Listener (Only works when pointerEvents is auto)
        // Click Listener (Only works when pointerEvents is auto)
        window.addEventListener('click', (event) => {
            console.log('Global Click Detected'); // DEBUG

            // FIX: On Mobile, pointerEvents might be 'none' because there is no hover.
            // But we still want to raycast for WebGL buttons.
            // The check below was blocking mobile interactions.
            // We will do a speculative raycast first.

            this.pointer.x = (event.clientX / window.innerWidth) * 2 - 1;
            this.pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;

            this.raycaster.setFromCamera(this.pointer, this.camera);
            const intersects = this.raycaster.intersectObjects(this.scene.children, true);

            if (intersects.length > 0) {
                // Check if we hit an interactive object
                let object = intersects[0].object;
                let isInteractive = false;
                // Traverse up to find userData
                let tmp = object;
                while (tmp && tmp !== this.scene) {
                    if (tmp.userData && (tmp.userData.isInteractable || tmp.userData.type === 'video-button' || tmp.userData.type === 'button')) {
                        isInteractive = true;
                        // Update reference to the interactive parent
                        object = tmp;
                        break;
                    }
                    tmp = tmp.parent;
                }

                if (isInteractive) {
                    console.log('Raycast hit interactive object despite pointerEvents state:', object.userData);
                    // Proceed to handler logic...
                } else {
                    // If NOT interactive, AND pointerEvents was 'none', then we should indeed return
                    // to let CSS handle it (if any).
                    if (this.renderer.domElement.style.pointerEvents === 'none') {
                        console.log('Pointer Events NONE & No Interactive Hit - Raycast Skipped');
                        return;
                    }
                }

                // Copy-paste existing handler logic or use the found 'object'
                // The existing logic below iterates again. We can simplify.
                // Let's just remove the blocker at the top and let the existing logic run?
                // But valid CSS clicks must pass through.
                // If we Raycast and hit nothing relevant, we do nothing.
                // If we Raycast and hit a 'video-button', we trigger it.
            } else {
                if (this.renderer.domElement.style.pointerEvents === 'none') return;
            }

            // RE-RUN existing logic block but without the early return?
            // Safer: Just bypass the early return if we find a 'video-button' match
            // ...
            // Let's rewrite the block to be cleaner.

            /* ORIGINAL CODE WAS:
            if (this.renderer.domElement.style.pointerEvents === 'none') {
                console.log('Pointer Events NONE - Raycast Skipped or Handled by CSS');
                return;
            }
            */

            // NEW CODE:
            // We removed the early return. We will decide to act based on Raycast result.

            console.log('Raycast Hits:', intersects.length);

            if (intersects.length > 0) {
                let object = intersects[0].object;
                console.log('First Hit:', object.name || object.type);

                while (object && object !== this.scene) {
                    if (object.userData) {
                        // ... logic ...

                        console.log('Checking UserData:', object.userData); // DEBUG
                        // Content Hub Wall Click DISABLED as per request
                        /*
                        if (object.userData.type === 'content-hub') {
                            console.log('Dispatching content-hub event', object.userData.sectionId);
                            window.dispatchEvent(new CustomEvent('open-section-modal', {
                                detail: { sectionId: object.userData.sectionId }
                            }));
                            break;
                        }
                        */
                        // Handle Generic Button (Discover)
                        if (object.userData.type === 'button' && object.userData.action === 'discover') {
                            console.log('Dispatching discover button event', object.userData.sectionId);
                            window.dispatchEvent(new CustomEvent('open-section-modal', {
                                detail: { sectionId: object.userData.sectionId }
                            }));
                            break;
                        }
                        // Handle Video Button Click
                        if (object.userData.type === 'video-button') {
                            window.dispatchEvent(new CustomEvent('open-video-lightbox', {
                                detail: { src: object.userData.videoId, type: 'youtube' }
                            }));
                            break;
                        }
                        // Video interaction is now handled natively by YouTube iframe
                    }
                    object = object.parent;
                }
            }
        });
    }

    setupRenderer() {
        // CSS3D Renderer on BOTTOM (z-index 0)
        this.cssRenderer.setSize(this.width, this.height);
        this.cssRenderer.domElement.style.position = 'absolute';
        this.cssRenderer.domElement.style.top = '0';
        this.cssRenderer.domElement.style.zIndex = '0';
        this.container.appendChild(this.cssRenderer.domElement);

        // WebGL Renderer Setup
        this.renderer.setSize(this.width, this.height);

        // OPTIMIZATION: Cap Pixel Ratio on Mobile to prevent OOM/Crash
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const maxPixelRatio = isMobile ? 1 : 2;

        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxPixelRatio));

        // WebGL Renderer on TOP (z-index 1) to allow occlusion of CSS objects
        this.renderer.domElement.style.position = 'absolute';
        this.renderer.domElement.style.top = '0';
        this.renderer.domElement.style.zIndex = '1';
        // Default to no pointer events so clicks pass through to CSS layer (videos)
        this.renderer.domElement.style.pointerEvents = 'none';
        this.container.appendChild(this.renderer.domElement);
    }

    setupCamera() {
        this.camera.position.z = 5;
    }

    async addObjects() {
        this.sections = [];
        this.queuedData = []; // Store data for deferred loading

        try {
            console.log("Fetching data.json...");
            const response = await fetch('data.json?t=' + Date.now());
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const data = await response.json();
            console.log("Data loaded:", data);

            const sectionData = data.sections;

            // Start at -10 so the first section (placed at -currentZ = 10)
            // covers the camera at z=5.
            let currentZ = -10;

            // PRE-CALCULATION: Calculate Total World Length IMMEDIATELY
            // This prevents the "Scroll Jump" / "Double Load" feeling on mobile
            // when background sections finish loading.
            let projectedZ = currentZ;
            for (let i = 0; i < sectionData.length; i++) {
                projectedZ += Section.calculateLength(sectionData[i], i);
            }
            this.totalLength = projectedZ;
            console.log(`[Startup] Pre-calculated Total Length: ${this.totalLength}`);

            // Dispatch event with non-zero length so ScrollManager sets full height NOW
            // We do this BEFORE loading items.
            window.dispatchEvent(new CustomEvent('world-ready'));

            // PROGRESSIVE INITIALIZATION (Mobile Startup Optimization)
            // 1. Critical Path: Load only first 1 sections (Home Only)
            // This ensures maximal fluidity during initial render
            const initialLoadCount = 1;

            for (let i = 0; i < sectionData.length; i++) {
                if (i < initialLoadCount) {
                    try {
                        const section = new Section(sectionData[i], i, currentZ);
                        this.scene.add(section.group);
                        this.sections.push(section);
                        currentZ += section.length;

                        // Force Active on First Section for Instant Video Playback
                        if (i === 0) {
                            console.log(`[Startup] Activating Section 0 immediately`);
                            section.setActive(true);
                        }

                        console.log(`[Startup] Loaded Critical Section ${i}`);
                    } catch (e) {
                        console.error(`Error creating section ${i}:`, e);
                        // Even if it fails, we must advance Z to match pre-calc
                        currentZ += Section.calculateLength(sectionData[i], i);
                    }
                } else {
                    // Queue the rest for Deferred Loading
                    this.queuedData.push({
                        data: sectionData[i],
                        index: i,
                        // We must track StartZ correctly here because we aren't instantiating yet
                        // But we can calculate it relative to the loop!
                        // Actually, better to just let the loader handle Z tracking if we supply the start.
                        // But wait, if we queue them, we can't update currentZ for the *next* queued item 
                        // unless we calculate length now.
                    });

                    // IMPORTANT: Ensure expected Z alignment matches pre-calc
                    // We don't instantiate, but we increment our tracker
                    const len = Section.calculateLength(sectionData[i], i);
                    currentZ += len;
                }
            }

            // We need to pass the INITIAL currentZ state to the deferred loader?
            // No, the deferred loader needs to know where the *First Queued Item* starts.
            // But we just incremented 'currentZ' all the way to the end in the loop above.
            // So we need to reset or track "Where do we start loading the rest?"

            // Re-calc where the queue starts:
            let queueStartZ = -10;
            for (let i = 0; i < initialLoadCount; i++) {
                queueStartZ += Section.calculateLength(sectionData[i], i);
            }
            this.nextLoadZ = queueStartZ;

            // Console log status
            console.log(`[Startup] Critical Sections ready. Queued: ${this.queuedData.length}`);

            // Store total length (Temporary, will update as we load)
            // this.totalLength = currentZ; // ALREADY SET PRE-CALC

            // console.log("World ready, dispatching event.");
            // Dispatch event that world is ready
            // window.dispatchEvent(new CustomEvent('world-ready')); // MOVED UP

            // 2. Start Deferred Loading (Background)
            if (this.queuedData.length > 0) {
                // Wait longer (200ms) to ensure initial render is done
                setTimeout(() => {
                    this.loadRemainingSections();
                }, 200);
            }

        } catch (error) {
            console.error('Failed to load data:', error);
            // Add a fallback text to scene if data fails
            const errorDiv = document.createElement('div');
            errorDiv.style.position = 'fixed';
            errorDiv.style.top = '50%';
            errorDiv.style.left = '50%';
            errorDiv.style.transform = 'translate(-50%, -50%)';
            errorDiv.style.color = 'red';
            errorDiv.style.fontSize = '24px';
            errorDiv.innerText = 'Error loading content. Check console.';
            document.body.appendChild(errorDiv);
        }
    }

    loadRemainingSections() {
        if (this.queuedData.length === 0) {
            console.log("[Startup] All sections loaded!");
            return;
        }

        // Process ONE section
        const item = this.queuedData.shift(); // Get next

        try {
            // Retrieve the current Z tracking variable
            // Note: We ignored 'item.startZ' from the loop above because it was invalid/unknown
            // We use 'this.nextLoadZ' which tracks where the last section ended.
            const startZ = this.nextLoadZ;

            const section = new Section(item.data, item.index, startZ);
            this.scene.add(section.group);
            this.sections.push(section); // Add to main array

            // CORRECTLY sorting sections array?
            // Since we push in order (0, 1, then queue 2, 3...), push is fine.
            // But if we ever loaded out of order, we'd need this.sections[index] = section;
            // Let's ensure order:
            // this.sections[item.index] = section; // Safer if async messes up order (it shouldn't here)

            // Update Global Tracker
            this.nextLoadZ += section.length;
            // FIX: Do NOT overwrite totalLength with partial length during load.
            // We pre-calculated the correct full length in init().
            // this.totalLength = this.nextLoadZ;

            console.log(`[Background] Loaded Section ${item.index} (Z: ${startZ})`);

            // Dispatch Update Event so ScrollManager can recalculate height
            // NO! We pre-calculated totalLength correctly.
            // If we re-dispatch now, we might jitter the scroll if there's a tiny float diff.
            // Only dispatch if actual length differs (e.g. dynamic content changed it).
            // But here we want STABILITY to fix "Double Load".
            // So we suppress this event unless necessary.
            // window.dispatchEvent(new CustomEvent('world-updated'));

            // Schedule Next
            // Slow down to 200ms to allow GC and Main Thread breathing room
            setTimeout(() => this.loadRemainingSections(), 200);

        } catch (e) {
            console.error(`Error deferred loading section ${item.index}:`, e);
            // Skip and continue?
            setTimeout(() => this.loadRemainingSections(), 200);
        }
    }

    addLights() {
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        this.scene.add(ambientLight);

        const pointLight = new THREE.PointLight(0xffffff, 1);
        pointLight.position.set(5, 5, 5);
        this.scene.add(pointLight);
    }

    onResize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;

        this.camera.aspect = this.width / this.height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(this.width, this.height);
        this.cssRenderer.setSize(this.width, this.height);
    }

    rotateCamera(angle, isMobile = false) {
        let targetX = 0;

        // Desktop zooms out less (1.2) than mobile (2.0) for a closer feel
        const displacement = isMobile ? 2 : 1.2;

        if (angle > 0.1) targetX = displacement; // Looking Left -> Move Right (Backwards)
        if (angle < -0.1) targetX = -displacement; // Looking Right -> Move Left (Backwards)

        gsap.to(this.camera.rotation, {
            y: angle,
            duration: 0.5,
            ease: "power2.out"
        });

        gsap.to(this.camera.position, {
            x: targetX,
            duration: 0.5,
            ease: "power2.out"
        });
    }

    pause() {
        this.paused = true;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    resume() {
        this.paused = false;
        this.animate();
    }

    animate() {
        if (this.paused) return;

        this.animationId = requestAnimationFrame(this.animate.bind(this));

        if (this.sections) {
            this.sections.forEach(section => section.update());
        }



        this.renderer.render(this.scene, this.camera);
        this.cssRenderer.render(this.scene, this.camera);
    }
}
